// AWS SDK for interacting with AWS services
import { tokenRefreshManager } from "./tokenRefresh";
const AWS = require("aws-sdk");

// Cache for S3 client to avoid recreating on every call
let cachedS3Client = null;
let clientCreatedWithTokenAt = null; // Track when client was created relative to token refresh

/**
 * Check if the cached S3 client has fresh tokens
 * Uses the same logic as tokenRefreshManager.needsRefresh
 * @returns {boolean} True if cached client can be reused
 */
const isCachedClientFresh = () => {
  if (!cachedS3Client || !clientCreatedWithTokenAt) return false;

  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return false;

    const now = Date.now();

    // Check if token was refreshed after client was created
    if (user.tokenRefreshedAt && clientCreatedWithTokenAt < user.tokenRefreshedAt) {
      console.log('Cached S3 client is stale - token was refreshed after client creation');
      return false;
    }

    // Use tokenRefreshedAt if present (preferred method)
    if (user.tokenRefreshedAt) {
      const ageMs = now - user.tokenRefreshedAt;
      const maxAgeMs = 50 * 60 * 1000; // 50 minutes
      return ageMs <= maxAgeMs;
    }

    // Fallback: use access token / id token expiry with 2 minute safety window
    const token = user.accessToken || user.idToken;
    if (!token) return false;

    const [, payloadB64] = token.split(".");
    if (!payloadB64) return false;

    const payloadJson = atob(payloadB64);
    const payload = JSON.parse(payloadJson);
    const exp = typeof payload.exp === "number" ? payload.exp : null;

    if (!exp) return false;

    const expMs = exp * 1000;
    const safetyWindowMs = 2 * 60 * 1000; // refresh 2 minutes before expiry

    return expMs - now > safetyWindowMs;
  } catch (error) {
    console.error('Error checking cached client freshness:', error);
    return false;
  }
};

/**
 * Generates a unique filename for S3 uploads
 * @param {string} action - The type of action being performed (defaults to 'new_signal')
 * @returns {string} A unique filename containing timestamp, random string and action suffix
 */
const generateUniqueFileName = (action = 'new_signal') => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const actionSuffix = action === 'new_signal' ? '-ns' : '';

  return `${timestamp}-${randomSuffix}${actionSuffix}`;
};

/**
 * Creates fresh AWS credentials for S3 operations using the provided fresh user token
 * Uses cached client if token is still fresh (< 50 minutes old)
 * @param {Object} apiData - Contains AWS configuration like identity pool and user pool
 * @param {Object} user - User data containing fresh idToken
 * @returns {Object} S3 client instance
 */
const createS3Client = async (apiData) => {
  // Check if we have a cached client with fresh tokens
  if (isCachedClientFresh()) {
    console.log('Using cached S3 client (token still fresh)');
    return cachedS3Client;
  }

  // Use pool configuration from apiData
  const userPoolId = apiData?.api?.userPool;
  const identityPoolId = apiData?.api?.identityPool;

  // Ensure we have fresh tokens before creating AWS credentials
  const localUser = JSON.parse(localStorage.getItem("user"));
  const freshUser = await tokenRefreshManager.ensureFreshToken(localUser);

  // Set up Cognito credentials using identity pool and fresh user token
  const credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: identityPoolId,
    Logins: {
      [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${userPoolId}`]: freshUser?.idToken,
    },
  });

  // Configure AWS SDK with region and credentials
  AWS.config.update({
    region: process.env.REACT_APP_AWS_REGION,
    credentials: credentials,
  });

  // Get fresh AWS credentials
  try {
    await credentials.getPromise();
  } catch (error) {
    console.error('Error getting AWS credentials:', error);
    // If credentials still fail, try a forced refresh (should be rare)
    const refreshedUser = await tokenRefreshManager.refreshUserToken(freshUser);
    // Create new credentials with the refreshed token
    const retryCredentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: identityPoolId,
      Logins: {
        [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${userPoolId}`]: refreshedUser?.idToken,
      },
    });
    AWS.config.update({
      region: process.env.REACT_APP_AWS_REGION,
      credentials: retryCredentials,
    });
    await retryCredentials.getPromise();
  }

  // Create and cache the S3 client
  cachedS3Client = new AWS.S3({
    useAccelerateEndpoint: true
  });

  // Track when this client was created relative to token refresh
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    clientCreatedWithTokenAt = user?.tokenRefreshedAt || Date.now();
  } catch (error) {
    clientCreatedWithTokenAt = Date.now();
  }

  return cachedS3Client;
};

/**
 * Uploads data to S3 bucket
 * @param {Object} data - The data to upload
 * @param {Object} apiData - AWS configuration data
 * @param {Object} user - User authentication data
 * @param {string} action - Type of action being performed (defaults to 'new_signal')
 * @returns {Promise<Object>} Upload result containing success status and file details
 */
const uploadToS3 = async (data, apiData, user, action = 'new_signal') => {
  try {
    // Create S3 client with current credentials (reactive refresh on auth errors)
    const s3Client = await createS3Client(apiData);

    const dataString = JSON.stringify(data);
    const bucketName = apiData?.websocket?.socketBucket;

    if (!bucketName) {
      throw new Error("S3 bucket name not found in apiData");
    }

    let s3FileName = generateUniqueFileName(action);

    const uploadParams = {
      Body: dataString,
      Bucket: bucketName,
      ContentType: 'application/json',
      Key: s3FileName,
    };

    try {
      const response = await s3Client.upload(uploadParams).promise();
      return {
        success: true,
        fileName: s3FileName,
        location: response.Location
      };
    } catch (s3Error) {
      // Check if this is an authentication error that might need token refresh
      const isAuthError = s3Error.code === 'InvalidToken' ||
        s3Error.code === 'ExpiredToken' ||
        s3Error.code === 'InvalidAccessKeyId' ||
        s3Error.code === 'SignatureDoesNotMatch' ||
        s3Error.message?.includes('token') ||
        s3Error.message?.includes('credentials') ||
        s3Error.statusCode === 401 ||
        s3Error.statusCode === 400 ||
        s3Error.statusCode === 403;

      if (isAuthError) {
        console.log('S3 upload failed with auth error, attempting token refresh and retry');
        try {
          // Force a token refresh and retry with fresh token
          const localUser = JSON.parse(localStorage.getItem("user"));
          await tokenRefreshManager.refreshUserToken(localUser);

          // Retry with fresh token
          const retryS3Client = await createS3Client(apiData);
          const retryResponse = await retryS3Client.upload(uploadParams).promise();

          return {
            success: true,
            fileName: s3FileName,
            location: retryResponse.Location
          };
        } catch (retryError) {
          // Only log errors when online (Sentry can report them)
          if (navigator.onLine) {
            console.error('S3 upload retry failed:', retryError);
          }
          return {
            success: false,
            error: `Authentication failed: ${retryError.message}`
          };
        }
      } else {
        // Not an auth error, return original error
        throw s3Error;
      }
    }
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Downloads data from S3 bucket using chunked download for large files
 * @param {string} s3Link - The S3 key/file path
 * @param {string} bucket - Bucket type ('socket' or 'eod', defaults to 'socket')
 * @param {Object} apiData - AWS configuration data
 * @param {Object} user - User authentication data
 * @returns {Promise<string>} The file content as a string
 */
const readData = async (s3Link, bucket = "socket", apiData, user) => {
  let bucketName = apiData?.websocket?.socketBucket;
  if (bucket === "eod") {
    bucketName = apiData?.api?.eodBucket;
  }

  try {
    // Create S3 client with current credentials (reactive refresh on auth errors)
    const s3Client = await createS3Client(apiData);

    let headParams = {
      Bucket: bucketName,
      Key: s3Link,
    };

    // Get the file size
    let headData = await s3Client.headObject(headParams).promise();
    const fileSize = headData.ContentLength;
    const chunkSize = 1024 * 1024;
    const numChunks = Math.ceil(fileSize / chunkSize);

    // Create an array of promises for each chunk
    const chunkPromises = Array.from({ length: numChunks }, (_, index) => {
      const rangeStart = index * chunkSize;
      const rangeEnd = Math.min(rangeStart + chunkSize - 1, fileSize - 1);

      let params = {
        Bucket: bucketName,
        Key: s3Link,
        Range: `bytes=${rangeStart}-${rangeEnd}`,
      };

      return s3Client.getObject(params).promise().then(data => data.Body.toString("utf-8"));
    });

    // Wait for all chunks to be fetched
    try {
      const chunks = await Promise.all(chunkPromises);
      const fileContent = chunks.join('');
      return fileContent;
    } catch (s3Error) {
      // Check if this is an authentication error that might need token refresh
      const isAuthError = s3Error.code === 'InvalidToken' ||
        s3Error.code === 'ExpiredToken' ||
        s3Error.code === 'InvalidAccessKeyId' ||
        s3Error.code === 'SignatureDoesNotMatch' ||
        s3Error.message?.includes('token') ||
        s3Error.message?.includes('credentials') ||
        s3Error.statusCode === 401 ||
        s3Error.statusCode === 400 ||
        s3Error.statusCode === 403;

      if (isAuthError) {
        console.log('S3 download failed with auth error, attempting token refresh and retry');
        try {
          // Force a token refresh and retry with fresh token
          const localUser = JSON.parse(localStorage.getItem("user"));
          await tokenRefreshManager.refreshUserToken(localUser);

          // Retry with fresh token - recreate the entire download process
          const retryS3Client = await createS3Client(apiData);

          let retryHeadData = await retryS3Client.headObject(headParams).promise();
          const retryFileSize = retryHeadData.ContentLength;
          const retryChunkSize = 1024 * 1024;
          const retryNumChunks = Math.ceil(retryFileSize / retryChunkSize);

          const retryChunkPromises = Array.from({ length: retryNumChunks }, (_, index) => {
            const rangeStart = index * retryChunkSize;
            const rangeEnd = Math.min(rangeStart + retryChunkSize - 1, retryFileSize - 1);

            let params = {
              Bucket: bucketName,
              Key: s3Link,
              Range: `bytes=${rangeStart}-${rangeEnd}`,
            };

            return retryS3Client.getObject(params).promise().then(data => data.Body.toString("utf-8"));
          });

          const retryChunks = await Promise.all(retryChunkPromises);
          const retryFileContent = retryChunks.join('');
          return retryFileContent;
        } catch (retryError) {
          // Only log errors when online (Sentry can report them)
          if (navigator.onLine) {
            console.error('S3 download retry failed:', retryError);
          }
          throw new Error(`Authentication failed during download: ${retryError.message}`);
        }
      } else {
        // Not an auth error, re-throw original error
        throw s3Error;
      }
    }
  } catch (err) {
    // Only log errors when online (Sentry can report them)
    if (navigator.onLine) {
      console.error(`Error reading data from S3:`, err);
    }
    throw new Error(`S3 download failed: ${err.message}`);
  }
};

/**
 * Clears the cached S3 client - useful for logout or when forcing fresh credentials
 */
const clearS3ClientCache = () => {
  cachedS3Client = null;
  clientCreatedWithTokenAt = null;
  console.log('S3 client cache cleared');
};

/**
 * Reset S3 credentials - no longer needed with fresh credential creation per operation
 * Kept for backward compatibility
 */

/**
 * Sockets utility function - maintains backward compatibility
 * @param {Object} apiData - AWS configuration data
 * @param {Object} user - User authentication data
 * @returns {Object} Object containing readData function
 */
const Sockets = (apiData, user) => {
  return {
    readData: (s3Link, bucket = "socket") => readData(s3Link, bucket, apiData, user)
  };
};

// Export functions for backward compatibility
export { uploadToS3, clearS3ClientCache };
export default Sockets;
